import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, FileText } from 'lucide-angular';

export interface OrderItem {
  id: string;
  customerName: string;
  customerInitials: string;
  orderNumber: string;
  amount: number;
  status: 'completed' | 'pending' | 'processing' | 'cancelled' | 'refunded';
  date: Date;
  avatarColor?: string;
}

@Component({
  selector: 'app-recent-orders',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    <div class="bg-white rounded-xl border border-gray-200 p-6">
      <div class="flex items-center justify-between mb-6">
        <h3 class="text-lg font-semibold text-gray-900 font-outfit">{{ title }}</h3>
        <button class="text-sm text-brand-500 hover:text-brand-600 font-medium transition-colors">
          View All Orders
        </button>
      </div>
      
      <div class="space-y-4">
        @for (order of recentOrders; track order.id) {
          <div class="flex items-center justify-between py-3 hover:bg-gray-50 rounded-lg px-2 transition-colors cursor-pointer">
            <!-- Customer info -->
            <div class="flex items-center space-x-3 flex-1">
              <div 
                class="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                [style.background-color]="order.avatarColor || getAvatarColor(order.customerInitials)">
                {{ order.customerInitials }}
              </div>
              
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-gray-900 truncate">{{ order.customerName }}</p>
                <p class="text-xs text-gray-500">{{ order.orderNumber }}</p>
              </div>
            </div>
            
            <!-- Order details -->
            <div class="text-right flex-shrink-0 ml-4">
              <p class="text-sm font-semibold text-gray-900">{{ formatCurrency(order.amount) }}</p>
              <div class="mt-1">
                <span 
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  [class]="getStatusBadgeClass(order.status)">
                  {{ getStatusLabel(order.status) }}
                </span>
              </div>
              <p class="text-xs text-gray-500 mt-1">{{ formatDate(order.date) }}</p>
            </div>
          </div>
        }
      </div>
      
      @if (recentOrders.length === 0) {
        <div class="text-center py-12">
          <div class="text-gray-400 mb-3">
            <lucide-icon [img]="FileText" size="48" class="mx-auto"></lucide-icon>
          </div>
          <p class="text-sm text-gray-500">No recent orders</p>
          <p class="text-xs text-gray-400 mt-1">Orders will appear here once customers start purchasing</p>
        </div>
      }
      
      <!-- Summary stats -->
      @if (recentOrders.length > 0) {
        <div class="mt-6 pt-6 border-t border-gray-100">
          <div class="grid grid-cols-3 gap-4 text-center">
            <div>
              <p class="text-sm font-semibold text-gray-900">{{ recentOrders.length }}</p>
              <p class="text-xs text-gray-500">Total Orders</p>
            </div>
            <div>
              <p class="text-sm font-semibold text-gray-900">{{ formatCurrency(totalAmount) }}</p>
              <p class="text-xs text-gray-500">Total Value</p>
            </div>
            <div>
              <p class="text-sm font-semibold text-gray-900">{{ formatCurrency(averageAmount) }}</p>
              <p class="text-xs text-gray-500">Avg. Order</p>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: ``
})
export class RecentOrdersComponent {
  @Input() title: string = 'Recent Orders';
  @Input() recentOrders: OrderItem[] = [];

  // Icon references
  protected readonly FileText = FileText;

  get totalAmount(): number {
    return this.recentOrders.reduce((sum, order) => sum + order.amount, 0);
  }

  get averageAmount(): number {
    return this.recentOrders.length > 0 ? this.totalAmount / this.recentOrders.length : 0;
  }

  getStatusBadgeClass(status: string): string {
    const classes = {
      'completed': 'bg-success-100 text-success-800',
      'pending': 'bg-warning-100 text-warning-800',
      'processing': 'bg-blue-light-100 text-blue-light-800',
      'cancelled': 'bg-error-100 text-error-800',
      'refunded': 'bg-gray-100 text-gray-800'
    };
    return classes[status as keyof typeof classes] || 'bg-gray-100 text-gray-800';
  }

  getStatusLabel(status: string): string {
    const labels = {
      'completed': 'Completed',
      'pending': 'Pending',
      'processing': 'Processing',
      'cancelled': 'Cancelled',
      'refunded': 'Refunded'
    };
    return labels[status as keyof typeof labels] || status;
  }

  getAvatarColor(initials: string): string {
    const colors = [
      '#b55299', // brand-500
      '#f59e0b', // orange-500
      '#0ba5ec', // blue-light-500
      '#10b981', // success-500
      '#8b5cf6', // purple-500
      '#f97316', // orange-600
      '#06b6d4', // cyan-500
      '#84cc16'  // lime-500
    ];
    
    const hash = initials.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  formatDate(date: Date): string {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  }
}